import coordId from './coordId';
import { calcReal, calcImaginary, dragRectToBounds } from './pointer';
import { PALETTE_LUT, PALETTE_LUT_SIZE, INTERIOR_RGB } from './palette';

const MAX_ITERATIONS = 1000;
const WIDTH = 1200;
const HEIGHT = 900;

// Bailout radius 2^8. A high radius (vs. the classic 2) leaves the escaped |z|
// well past the boundary, which makes the continuous-count smoothing far cleaner
// — the fractional overshoot we subtract is more accurate. BAILOUT_SQ lets the
// escape loop compare squared magnitudes and skip the per-iteration sqrt.
const BAILOUT = 256;
const BAILOUT_SQ = BAILOUT * BAILOUT;
const LOG2 = Math.log(2);

// Linear supersampling factor: the escape maths + colouring run on a grid this
// many times finer in each axis, then we box-average SUPERSAMPLE^2 samples back
// down to each output pixel. This anti-aliases every edge — filigree, the set
// boundary, and the tiny embedded islands that would otherwise be hard 1px
// specks. Cost is ~SUPERSAMPLE^2× the render, paid once per zoom.
const SUPERSAMPLE = 2;

// sRGB byte → linear-light, for gamma-correct downsampling. Averaging colours in
// raw sRGB darkens high-contrast edges (a dark island speck against bright gold
// would crush toward black); averaging in linear light blends them correctly.
const SRGB_TO_LINEAR = new Float32Array(256);
for (let i = 0; i < 256; i++) {
    const c = i / 255;
    SRGB_TO_LINEAR[i] = c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
}

const linearToByte = (v) => {
    const s = v <= 0.0031308 ? 12.92 * v : 1.055 * Math.pow(v, 1 / 2.4) - 0.055;
    return Math.max(0, Math.min(255, Math.round(s * 255)));
};

// The single render seam. `generate()` resolves whatever the caller sent (a drag
// rectangle or manual axis values) down to one region's bounds, then everything
// funnels through here. Phase 2 swaps "render locally on a canvas" for "ask the
// server to render authoritatively" in this one function — no caller changes.
//
// The colour work runs on a SUPERSAMPLE×-finer grid, then downsamples:
//   1. Escape maths for every hi-res subpixel → a smoothed value (NaN = interior).
//   2. Histogram-equalise: rank the escaped subpixels and map each one's *rank*
//      (not its raw value) onto the palette, so the full ramp is used evenly no
//      matter how deep the zoom.
//   3. Box-average each SUPERSAMPLE×SUPERSAMPLE block, in linear light, into one
//      ImageData, blitted with a single putImageData — no per-pixel fillRect.
const renderRegion = (realMin, realMax, imaginaryMin, imaginaryMax, width, height) => {

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");

    const hiW = width * SUPERSAMPLE;
    const hiH = height * SUPERSAMPLE;
    const hiCount = hiW * hiH;

    // smoothed escape value per subpixel; NaN marks an interior (never-escaped)
    // point. NaN — not a sentinel number — because a very fast escape can yield a
    // legitimately negative smoothed value, which must not be mistaken for
    // interior.
    const smooth = new Float64Array(hiCount);
    const escaped = [];  // subpixel indices that escaped, for ranking

    // Step 1: the escape maths for every subpixel.
    for (let x = 0; x < hiW; x++) {
        const real = calcReal(x, realMin, realMax, hiW);
        for (let h = 0; h < hiH; h++) {
            const imaginary = calcImaginary(h, imaginaryMin, imaginaryMax, hiH);
            const value = escapeVelocity(real, imaginary);
            const idx = h * hiW + x;
            smooth[idx] = value;
            if (!Number.isNaN(value)) escaped.push(idx);
        }
    }

    // Step 2: histogram equalisation. Sorting the escaped indices by smoothed
    // value gives each subpixel a rank; its normalised rank is its position along
    // the ramp. Paint into a hi-res RGB buffer (opaque, so no alpha channel).
    escaped.sort((a, b) => smooth[a] - smooth[b]);

    const hiRGB = new Uint8ClampedArray(hiCount * 3);

    const [ir, ig, ib] = INTERIOR_RGB;
    for (let i = 0; i < hiCount; i++) {
        if (Number.isNaN(smooth[i])) {
            const p = i * 3;
            hiRGB[p] = ir;
            hiRGB[p + 1] = ig;
            hiRGB[p + 2] = ib;
        }
    }

    const denom = escaped.length > 1 ? escaped.length - 1 : 1;
    const maxLut = PALETTE_LUT_SIZE - 1;
    for (let k = 0; k < escaped.length; k++) {
        const lut = ((k / denom) * maxLut + 0.5) | 0;
        const lj = lut * 3;
        const p = escaped[k] * 3;
        hiRGB[p] = PALETTE_LUT[lj];
        hiRGB[p + 1] = PALETTE_LUT[lj + 1];
        hiRGB[p + 2] = PALETTE_LUT[lj + 2];
    }

    // Step 3: downsample. Average each SUPERSAMPLE×SUPERSAMPLE block in linear
    // light, then re-encode to sRGB for the output pixel.
    const image = ctx.createImageData(width, height);
    const data = image.data;
    const samples = SUPERSAMPLE * SUPERSAMPLE;

    for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
            let lr = 0;
            let lg = 0;
            let lb = 0;
            for (let dy = 0; dy < SUPERSAMPLE; dy++) {
                const hy = y * SUPERSAMPLE + dy;
                for (let dx = 0; dx < SUPERSAMPLE; dx++) {
                    const p = (hy * hiW + x * SUPERSAMPLE + dx) * 3;
                    lr += SRGB_TO_LINEAR[hiRGB[p]];
                    lg += SRGB_TO_LINEAR[hiRGB[p + 1]];
                    lb += SRGB_TO_LINEAR[hiRGB[p + 2]];
                }
            }
            const q = (y * width + x) * 4;
            data[q] = linearToByte(lr / samples);
            data[q + 1] = linearToByte(lg / samples);
            data[q + 2] = linearToByte(lb / samples);
            data[q + 3] = 255;
        }
    }

    ctx.putImageData(image, 0, 0);

    const zoom = {
        realMin: realMin,
        realMax: realMax,
        imaginaryMin: imaginaryMin,
        imaginaryMax: imaginaryMax,
        dataUrl: canvas.toDataURL()
    };
    zoom.id = coordId(zoom);

    canvas.remove();

    return zoom;
};

const generate = (

    zoomParams

) =>  {

    const width = zoomParams.width || WIDTH;
    const height = zoomParams.height || HEIGHT;

    if (zoomParams.manual) {

        let newMinR = zoomParams.axisValues.newMinR;
        let newMaxR = zoomParams.axisValues.newMaxR;
        let newMinI = zoomParams.axisValues.newMinI;
        let newMaxI = zoomParams.axisValues.newMaxI;

        return renderRegion(newMinR, newMaxR, newMinI, newMaxI, width, height);

    } else {

        // Pointer coords arrive already normalized to canvas pixels (see
        // Display.js); dragRectToBounds turns the drag rectangle into the new
        // complex-plane bounds.
        const dragRect = {
            x0: zoomParams.mousePositions.tlx,
            y0: zoomParams.mousePositions.tly,
            x1: zoomParams.mousePositions.brx,
            y1: zoomParams.mousePositions.bry
        };
        const currentBounds = {
            realMin: zoomParams.axisParams.realMin,
            realMax: zoomParams.axisParams.realmax,
            imaginaryMin: zoomParams.axisParams.imaginaryMin,
            imaginaryMax: zoomParams.axisParams.imaginaryMax
        };

        const b = dragRectToBounds(dragRect, currentBounds, width, height);

        return renderRegion(b.realMin, b.realMax, b.imaginaryMin, b.imaginaryMax, width, height);
    }
};

// The continuous (smoothed) escape count for c = real + imaginary·i. Returns NaN
// for interior points (never escaped within MAX_ITERATIONS).
//
// The integer iteration count is what produced the old banding — whole numbers
// step the colour in visible rings. Smoothing recovers a fractional count by
// measuring how far past the bailout radius |z| overshot on the final step:
//   n + 1 − log2( log|z| )
// which slides continuously between integer counts and kills the bands. The
// escape loop is the standard z ← z² + c, unrolled onto scalars (re, im and their
// squares) so it can test the squared magnitude and avoid a per-iteration sqrt.
function escapeVelocity(real, imaginary) {

    let re = 0;
    let im = 0;
    let re2 = 0;
    let im2 = 0;
    let n = 0;

    while (re2 + im2 <= BAILOUT_SQ && n < MAX_ITERATIONS) {
        im = 2 * re * im + imaginary;
        re = re2 - im2 + real;
        re2 = re * re;
        im2 = im * im;
        n++;
    }

    if (n >= MAX_ITERATIONS) return NaN;  // interior

    const magnitude = Math.sqrt(re2 + im2);
    return n + 1 - Math.log(Math.log(magnitude)) / LOG2;
}


export default generate;